import React, { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Play, Pause } from '@videoblocks/react-icons';

import { useVideoSelection } from '../context/VideoEditorContext';

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
}

interface VideoPlayerProps {
  src: string;
  isPlaying: boolean;
  onPlayPause: () => void;
  onTimeUpdate: (currentTime: number) => void;
  className?: string;
  currentTime?: number; // External time control for seeking
}

export default function VideoPlayer({
  src,
  isPlaying,
  onPlayPause,
  onTimeUpdate,
  className = '',
  currentTime,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [displayTime, setDisplayTime] = useState(0);

  const { selection } = useVideoSelection();

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setVideoDuration(video.duration);
      setIsVideoReady(true);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      onTimeUpdate(video.currentTime);
      setDisplayTime(video.currentTime);

      // If we have a selection and we've reached the end, loop
      if (selection && video.currentTime >= selection.endTime) {
        video.currentTime = selection.startTime;
        onTimeUpdate(video.currentTime);
        setDisplayTime(video.currentTime);
        video.play();
      }
    }
  }, [onTimeUpdate, selection]);

  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (video && selection) {
      // If we're at or past the end of selection, restart from beginning
      if (
        video.currentTime >= selection.endTime ||
        video.currentTime < selection.startTime
      ) {
        video.currentTime = selection.startTime;
      }
    }
  }, [selection]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideoReady) return;

    if (isPlaying) {
      video.play();
    } else {
      video.pause();
    }
  }, [isPlaying, isVideoReady]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && selection && isVideoReady) {
      // If current time is outside selection, move to start of selection
      if (
        video.currentTime < selection.startTime ||
        video.currentTime > selection.endTime
      ) {
        video.currentTime = selection.startTime;
      }
    }
  }, [selection, isVideoReady]);

  // Handle external currentTime updates (e.g., from dragging selection indicator)
  useEffect(() => {
    const video = videoRef.current;
    if (video && isVideoReady && currentTime !== undefined) {
      // Only seek if the external time is different from current video time
      if (Math.abs(video.currentTime - currentTime) > 0.1) {
        video.currentTime = currentTime;
        setDisplayTime(currentTime);
      }
    }
  }, [currentTime, isVideoReady]);

  // Keyboard controls
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const video = videoRef.current;
      if (!video || !isVideoReady) return;

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();
          onPlayPause();
          break;
      }
    },
    [onPlayPause, selection, isVideoReady, videoDuration]
  );

  // Handle container click for play/pause
  const handleContainerClick = () => {
    onPlayPause();
  };

  return (
    <div
      className={twMerge(
        'relative flex justify-center focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50',
        className
      )}
      onClick={handleContainerClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label="Video player - press space to play/pause"
    >
      <video
        ref={videoRef}
        src={src}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        className="object-contain rounded-t-2xl"
        preload="metadata"
        muted
      />

      {/* Bottom gradient overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black to-transparent pointer-events-none" />

      {isVideoReady && (
        <div className="absolute bottom-3 left-3 flex items-center gap-2 pointer-events-auto">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPlayPause();
            }}
            className="flex items-center justify-center hover:bg-white hover:bg-opacity-10 rounded p-1 transition-colors"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 fill-white" />
            ) : (
              <Play className="w-4 h-4 fill-white" />
            )}
          </button>
          <div
            className="text-white text-sm font-sans"
            data-testid="video-time-display"
          >
            {formatTime(displayTime)} / {formatTime(videoDuration)}
          </div>
        </div>
      )}
    </div>
  );
}
